import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Refresh,
  ExpandMore,
  Sync,
  SyncDisabled,
  CloudDone,
  CloudOff,
  Warning,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';
import electronAPI from '../services/electronAPI';

const SyncStatus = () => {
  const { t } = useI18n();
  const [syncStatus, setSyncStatus] = useState({
    isOnline: false,
    lastSync: null,
    pendingItems: 0,
    syncProgress: 0,
    isSyncing: false
  });
  const [syncHistory, setSyncHistory] = useState([]);
  const [serverStatus, setServerStatus] = useState({
    isReachable: false,
    connectedDevices: 0,
    totalDevices: 0
  });
  const [loading, setLoading] = useState(false);
  const [clearQueueDialog, setClearQueueDialog] = useState(false);

  const loadSyncStatus = async () => {
    setLoading(true);
    try {
      // Отримуємо статус синхронізації
      const status = await electronAPI.getSyncStatus();
      setSyncStatus(status);

      // Отримуємо історію синхронізації
      const history = await electronAPI.getSyncHistory();
      setSyncHistory(history || []);

      // Тимчасово імітуємо статус сервера
      setServerStatus({
        isReachable: true,
        connectedDevices: Math.floor(Math.random() * 5),
        totalDevices: 10
      });

    } catch (error) {
      console.error('Failed to load sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSyncStatus();
    
    // Оновлюємо статус кожні 10 секунд
    const interval = setInterval(loadSyncStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const forceSync = async () => {
    try {
      await electronAPI.forceSync();
      setSyncStatus(prev => ({ ...prev, isSyncing: true }));
      
      // Чекаємо трохи і оновлюємо статус
      setTimeout(loadSyncStatus, 2000);
    } catch (error) {
      console.error('Force sync failed:', error);
    }
  };

  const openClearQueueDialog = () => {
    setClearQueueDialog(true);
  };

  const closeClearQueueDialog = () => {
    setClearQueueDialog(false);
  };

  const clearSyncQueue = async () => {
    try {
      await electronAPI.clearSyncQueue();
      await loadSyncStatus();
      closeClearQueueDialog();
    } catch (error) {
      console.error('Failed to clear sync queue:', error);
    }
  };

  const getSyncStatusColor = () => {
    if (!syncStatus.isOnline) return 'error';
    if (syncStatus.pendingItems > 0) return 'warning';
    if (syncStatus.isSyncing) return 'info';
    return 'success';
  };

  const getSyncStatusText = () => {
    if (!syncStatus.isOnline) return t('syncStatus.offline');
    if (syncStatus.isSyncing) return t('syncStatus.syncing');
    if (syncStatus.pendingItems > 0) return t('syncStatus.pending');
    return t('syncStatus.upToDate');
  };

  const getSyncIcon = () => {
    if (!syncStatus.isOnline) return <CloudOff />;
    if (syncStatus.isSyncing) return <Sync />;
    if (syncStatus.pendingItems > 0) return <Warning />;
    return <CloudDone />;
  };

  const getSyncItemStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'syncing': return <Sync color="info" />;
      default: return <SyncDisabled color="disabled" />;
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        🔄 {t('syncStatus.title')}
      </Typography>

      <Grid container spacing={3}>
        {/* Current Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {t('syncStatus.currentStatus')}
                </Typography>
                <Button
                  startIcon={<Refresh />}
                  onClick={loadSyncStatus}
                  disabled={loading}
                >
                  {t('common.refresh')}
                </Button>
              </Box>

              <Box display="flex" alignItems="center" mb={2}>
                {getSyncIcon()}
                <Box ml={1}>
                  <Typography variant="body1" fontWeight="bold">
                    {getSyncStatusText()}
                  </Typography>
                  <Chip
                    label={syncStatus.isOnline ? t('syncStatus.online') : t('syncStatus.offline')}
                    color={syncStatus.isOnline ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>

              {syncStatus.isSyncing && (
                <Box mb={2}>
                  <LinearProgress 
                    variant={syncStatus.syncProgress > 0 ? "determinate" : "indeterminate"}
                    value={syncStatus.syncProgress}
                  />
                  <Typography variant="caption" display="block" textAlign="center" mt={1}>
                    {syncStatus.syncProgress > 0 ? `${syncStatus.syncProgress}%` : t('syncStatus.processing')}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {t('syncStatus.pendingItems')}
                  </Typography>
                  <Typography variant="h6" color={syncStatus.pendingItems > 0 ? 'warning.main' : 'success.main'}>
                    {syncStatus.pendingItems}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {t('syncStatus.lastSync')}
                  </Typography>
                  <Typography variant="body2">
                    {syncStatus.lastSync 
                      ? new Date(syncStatus.lastSync).toLocaleString()
                      : t('common.never')
                    }
                  </Typography>
                </Grid>
              </Grid>

              <Box mt={2} display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<Sync />}
                  onClick={forceSync}
                  disabled={!syncStatus.isOnline || syncStatus.isSyncing}
                  fullWidth
                >
                  {t('syncStatus.forceSync')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={openClearQueueDialog}
                  disabled={syncStatus.pendingItems === 0}
                >
                  {t('syncStatus.clearQueue')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Server Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('syncStatus.serverStatus')}
              </Typography>

              <Box display="flex" alignItems="center" mb={2}>
                <Chip
                  label={serverStatus.isReachable ? t('syncStatus.reachable') : t('syncStatus.unreachable')}
                  color={serverStatus.isReachable ? 'success' : 'error'}
                  icon={serverStatus.isReachable ? <CheckCircle /> : <ErrorIcon />}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {t('syncStatus.connectedDevices')}
                  </Typography>
                  <Typography variant="h6">
                    {serverStatus.connectedDevices}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {t('syncStatus.totalDevices')}
                  </Typography>
                  <Typography variant="h6">
                    {serverStatus.totalDevices}
                  </Typography>
                </Grid>
              </Grid>

              {!serverStatus.isReachable && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t('syncStatus.serverUnreachable')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sync History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('syncStatus.syncHistory')}
              </Typography>

              {syncHistory.length === 0 ? (
                <Typography color="textSecondary" textAlign="center" py={3}>
                  {t('syncStatus.noSyncHistory')}
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('syncStatus.timestamp')}</TableCell>
                        <TableCell>{t('syncStatus.operation')}</TableCell>
                        <TableCell>{t('syncStatus.table')}</TableCell>
                        <TableCell>{t('syncStatus.recordId')}</TableCell>
                        <TableCell>{t('syncStatus.status')}</TableCell>
                        <TableCell>{t('syncStatus.details')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syncHistory.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {item.timestamp ? new Date(item.timestamp).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.operation || 'unknown'}
                              size="small"
                              color={
                                item.operation === 'insert' ? 'primary' :
                                item.operation === 'update' ? 'secondary' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{item.table || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {item.recordId ? `${item.recordId.slice(0, 8)}...` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {getSyncItemStatusIcon(item.status)}
                              <Typography variant="body2" sx={{ ml: 1 }}>
                                {item.status ? t(`syncStatus.${item.status}`) : 'unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {item.error && (
                              <Typography variant="caption" color="error">
                                {item.error}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sync Statistics */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">
                {t('syncStatus.statistics')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main">
                        {syncHistory.filter(item => item.status === 'completed').length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('syncStatus.successfulSyncs')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {syncHistory.filter(item => item.status === 'failed').length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('syncStatus.failedSyncs')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {syncHistory.length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('syncStatus.totalOperations')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {syncHistory.length > 0 
                          ? Math.round((syncHistory.filter(item => item.status === 'completed').length / syncHistory.length) * 100)
                          : 0
                        }%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('syncStatus.successRate')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Clear Queue Dialog */}
      <Dialog
        open={clearQueueDialog}
        onClose={closeClearQueueDialog}
      >
        <DialogTitle>
          {t('syncStatus.clearQueue')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('syncStatus.confirmClearQueue')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearQueueDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={clearSyncQueue} color="error" variant="contained">
            {t('syncStatus.clearQueue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SyncStatus;